import { TagsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as TagsActions from './tags.actions';

@Injectable()
export class TagsEffects {
  private actions$ = inject(Actions);
  private tagsApi = inject(TagsApiService);
  private notification = inject(NotificationService);

  loadTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.loadTags),
      switchMap(({ params }) =>
        this.tagsApi.findAll(params).pipe(
          map((response) => TagsActions.loadTagsSuccess({
            tags: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_tags_error');
            return of(TagsActions.loadTagsFailure({ error: error.message || 'Failed to load tags' }));
          }),
        ),
      ),
    ),
  );

  createTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.createTag),
      switchMap(({ tagData }) =>
        this.tagsApi.create(tagData).pipe(
          map((tag) => {
            this.notification.success('admin.notifications.tag_created');
            return TagsActions.createTagSuccess({ tag });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_tag_error');
            return of(TagsActions.createTagFailure({ error: error.message || 'Failed to create tag' }));
          }),
        ),
      ),
    ),
  );

  updateTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.updateTag),
      switchMap(({ id, tagData }) =>
        this.tagsApi.update(id, tagData).pipe(
          map((tag) => {
            this.notification.success('admin.notifications.tag_updated');
            return TagsActions.updateTagSuccess({ tag });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_tag_error');
            return of(TagsActions.updateTagFailure({ error: error.message || 'Failed to update tag' }));
          }),
        ),
      ),
    ),
  );

  deleteTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.deleteTag),
      switchMap(({ id }) =>
        this.tagsApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.tag_deleted');
            return TagsActions.deleteTagSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_tag_error');
            return of(TagsActions.deleteTagFailure({ error: error.message || 'Failed to delete tag' }));
          }),
        ),
      ),
    ),
  );
}
